import _extends from '@babel/runtime/helpers/extends';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import React from 'react';
import PropTypes from 'prop-types';
import { DecorationSet, Decoration, EditorView } from 'prosemirror-view';
export { EditorView as ProseMirrorView } from 'prosemirror-view';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _styled from '@emotion/styled-base';
import { Schema, Node, DOMSerializer, DOMParser } from 'prosemirror-model';
import { schema as schema$1, MarkdownParser, defaultMarkdownParser, defaultMarkdownSerializer } from 'prosemirror-markdown';
import { addMentionNodes, getMentionsPlugin } from 'prosemirror-mentions';
import { toggleMark, wrapIn, setBlockType, chainCommands, exitCode, baseKeymap } from 'prosemirror-commands';
import { Field as Field$1, CloseModalButton, Modal, TextInput, Button, Content as Content$1 } from '@hundred5/design-system';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { undo, redo, history } from 'prosemirror-history';
import { wrapInList, splitListItem, liftListItem, sinkListItem } from 'prosemirror-schema-list';
import _asyncToGenerator from '@babel/runtime/helpers/asyncToGenerator';
import _regeneratorRuntime from '@babel/runtime/regenerator';
import _toConsumableArray from '@babel/runtime/helpers/toConsumableArray';
import { css } from 'emotion';
import { PluginKey, Plugin, EditorState } from 'prosemirror-state';
import selectFiles from 'select-files';
import { wrappingInputRule, textblockTypeInputRule, InputRule, smartQuotes, emDash, ellipsis, undoInputRule, inputRules } from 'prosemirror-inputrules';
import { keymap } from 'prosemirror-keymap';
import { dropCursor } from 'prosemirror-dropcursor';
import { gapCursor } from 'prosemirror-gapcursor';
import _classCallCheck from '@babel/runtime/helpers/classCallCheck';
import _createClass from '@babel/runtime/helpers/createClass';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import TextareaAutosize from 'react-textarea-autosize';

/**
 *
 * @param {OrderedMap} nodes
 * @returns {OrderedMap}
 */
function addTemplateVariableNodes(nodes) {
  return nodes.append({
    template_variable: templateVariableNode
  });
}

/**
 * See https://prosemirror.net/docs/ref/#model.NodeSpec
 */
var templateVariableNode = {
  group: "inline",
  inline: true,
  atom: true,
  attrs: {
    variable: ""
  },
  // selectable: false,
  draggable: false,
  toDOM: function toDOM(node) {
    var formattedText = node.attrs.variable.replace(/([A-Z])/g, " $1").toLowerCase();
    return ["span", {
      "data-template-variable": node.attrs.variable,
      class: "prosemirror-template-variable-node"
    }, "".concat(formattedText)];
  },
  parseDOM: [{
    // match tag with following CSS Selector
    tag: "span[data-template-variable]",
    getAttrs: function getAttrs(dom) {
      var templateVariable = dom.getAttribute("data-template-variable");
      return {
        variable: templateVariable
      };
    }
  }]
};

/**
 *
 * @param {OrderedMap} nodes
 * @returns {OrderedMap}
 */
function addSchedulingLinkNodes(nodes) {
  return nodes.append({
    scheduling_link: schedulingLinkNode
  });
}

/**
 * See https://prosemirror.net/docs/ref/#model.NodeSpec
 */
var schedulingLinkNode = {
  group: "inline",
  inline: true,
  atom: true,
  attrs: {
    link: ""
  },
  // selectable: false,
  draggable: false,
  toDOM: function toDOM(node) {
    return ["a", {
      href: node.attrs.link,
      "data-scheduling-link": node.attrs.link,
      "data-scheduling-link-tooltip": true,
      class: "prosemirror-scheduling-link-node"
    }, 'Scheduling link'];
  },
  parseDOM: [{
    // match tag with following CSS Selector
    tag: "a[data-scheduling-link]",
    getAttrs: function getAttrs(dom) {
      var schedulingLink = dom.getAttribute("data-scheduling-link");
      return {
        link: schedulingLink
      };
    }
  }]
};

var schema = new Schema({
  nodes: addTemplateVariableNodes(addSchedulingLinkNodes(addMentionNodes(schema$1.spec.nodes))),
  marks: patchStrongMark(schema$1.spec.marks)
});

/*
 * The schema from prosemirror-markdown does not include a fix added to
 * prosemirror-schema-basic, which handles parsing HTML pasted from Google Docs.
 * The HTML is wrapped with `<b style="font-weight: normal">`, which is parsed
 * into a normal strong mark, making the whole pasted content bold. This fix
 * ignores these specific `<b>` elements when parsing the HTML and patches the
 * definition of the strong mark from prosemirror-markdown.
 */
function patchStrongMark(marks) {
  return marks.update("strong", {
    parseDOM: [{
      tag: "strong"
    }, {
      tag: "b",
      getAttrs: function getAttrs(node) {
        return node.style.fontWeight != "normal" && null;
      }
    }, {
      style: "font-weight",
      getAttrs: function getAttrs(value) {
        return /^(bold(er)?|[5-9]\d{2,})$/.test(value) && null;
      }
    }],
    group: "block",
    toDOM: function toDOM() {
      return ["strong"];
    }
  });
}

var StrongIcon = function StrongIcon(props) {
  return /*#__PURE__*/React.createElement("svg", _extends({
    width: 20,
    height: 21,
    viewBox: "0 0 20 21",
    fill: "none"
  }, props), /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M13 9.492c.808-.559 1.375-1.475 1.375-2.325 0-1.884-1.458-3.334-3.333-3.334H5.833V15.5H11.7c1.742 0 3.092-1.417 3.092-3.158 0-1.267-.717-2.35-1.792-2.85zM8.333 5.917h2.5c.692 0 1.25.558 1.25 1.25 0 .691-.558 1.25-1.25 1.25h-2.5v-2.5zm0 7.5h2.917c.692 0 1.25-.559 1.25-1.25 0-.692-.558-1.25-1.25-1.25H8.333v2.5z",
    fill: "#000"
  }));
};
var EmphasisIcon = function EmphasisIcon(props) {
  return /*#__PURE__*/React.createElement("svg", _extends({
    width: 20,
    height: 21,
    viewBox: "0 0 20 21",
    fill: "none"
  }, props), /*#__PURE__*/React.createElement("path", {
    d: "M8.333 3.833v2.5h1.842L7.325 13H5v2.5h6.667V13H9.825l2.85-6.667H15v-2.5H8.333z",
    fill: "#000"
  }));
};
var InlineCodeIcon = function InlineCodeIcon(props) {
  return /*#__PURE__*/React.createElement("svg", _extends({
    width: 20,
    height: 21,
    viewBox: "0 0 20 21",
    fill: "none"
  }, props), /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M4 10.5l3.833 3.833L6.667 15.5l-5-5 5-5 1.166 1.167L4 10.5zm12 0l-3.833 3.833 1.166 1.167 5-5-5-5-1.166 1.167L16 10.5z",
    fill: "#000"
  }));
};
var LinkIcon = function LinkIcon(props) {
  return /*#__PURE__*/React.createElement("svg", _extends({
    width: 20,
    height: 21,
    viewBox: "0 0 20 21",
    fill: "none"
  }, props), /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M3.25 10.5a2.586 2.586 0 012.583-2.583h3.334V6.333H5.833A4.168 4.168 0 001.667 10.5c0 2.3 1.866 4.167 4.166 4.167h3.334v-1.584H5.833A2.586 2.586 0 013.25 10.5zm3.417.833h6.666V9.667H6.667v1.666zm4.166-5h3.334c2.3 0 4.166 1.867 4.166 4.167s-1.866 4.167-4.166 4.167h-3.334v-1.584h3.334A2.586 2.586 0 0016.75 10.5a2.586 2.586 0 00-2.583-2.583h-3.334V6.333z",
    fill: "#000"
  }));
};
var UndoIcon = function UndoIcon(props) {
  return /*#__PURE__*/React.createElement("svg", _extends({
    width: 20,
    height: 21,
    viewBox: "0 0 20 21",
    fill: "none"
  }, props), /*#__PURE__*/React.createElement("path", {
    d: "M10.417 7.167a8.72 8.72 0 00-5.75 2.166l-3-3v7.5h7.5L6.15 10.817C7.308 9.85 8.783 9.25 10.417 9.25a6.676 6.676 0 016.333 4.583l1.975-.65c-1.158-3.491-4.433-6.016-8.308-6.016z",
    fill: "#000"
  }));
};
var RedoIcon = function RedoIcon(props) {
  return /*#__PURE__*/React.createElement("svg", _extends({
    width: 20,
    height: 21,
    viewBox: "0 0 20 21",
    fill: "none"
  }, props), /*#__PURE__*/React.createElement("path", {
    d: "M15.333 9.333a8.72 8.72 0 00-5.75-2.166c-3.875 0-7.15 2.525-8.3 6.016l1.967.65A6.669 6.669 0 019.583 9.25c1.625 0 3.109.6 4.267 1.567l-3.017 3.016h7.5v-7.5l-3 3z",
    fill: "#000"
  }));
};
var NumberListIcon = function NumberListIcon(props) {
  return /*#__PURE__*/React.createElement("svg", _extends({
    width: 20,
    height: 21,
    viewBox: "0 0 20 21",
    fill: "none"
  }, props), /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M3.333 7.167H2.5v-2.5h-.833v-.834h1.666v3.334zm0 7.5H1.667v-.834h2.5v3.334h-2.5v-.834h1.666v-.416H2.5v-.834h.833v-.416zm-1.666-5h1.5l-1.5 1.75v.75h2.5v-.834h-1.5l1.5-1.75v-.75h-2.5v.834zm4.166-3.334V4.667H17.5v1.666H5.833zm0 10H17.5v-1.666H5.833v1.666zm11.667-5H5.833V9.667H17.5v1.666z",
    fill: "#000"
  }));
};
var BulletListIcon = function BulletListIcon(props) {
  return /*#__PURE__*/React.createElement("svg", _extends({
    width: 20,
    height: 21,
    viewBox: "0 0 20 21",
    fill: "none"
  }, props), /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M2.083 5.5c0-.692.559-1.25 1.25-1.25.692 0 1.25.558 1.25 1.25s-.558 1.25-1.25 1.25c-.691 0-1.25-.558-1.25-1.25zm0 5c0-.692.559-1.25 1.25-1.25.692 0 1.25.558 1.25 1.25s-.558 1.25-1.25 1.25c-.691 0-1.25-.558-1.25-1.25zm1.25 3.75c-.691 0-1.25.567-1.25 1.25s.567 1.25 1.25 1.25a1.26 1.26 0 001.25-1.25c0-.683-.558-1.25-1.25-1.25zM17.5 16.333H5.833v-1.666H17.5v1.666zm-11.667-5H17.5V9.667H5.833v1.666zm0-5V4.667H17.5v1.666H5.833z",
    fill: "#000"
  }));
};
var QuoteIcon = function QuoteIcon(props) {
  return /*#__PURE__*/React.createElement("svg", _extends({
    width: 20,
    height: 21,
    viewBox: "0 0 20 21",
    fill: "none"
  }, props), /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M6.667 14.667h-2.5l1.666-3.334h-2.5v-5h5v5l-1.666 3.334zm8.333 0h-2.5l1.667-3.334h-2.5v-5h5v5L15 14.667z",
    fill: "#000"
  }));
};
var ImageIcon = function ImageIcon(props) {
  return /*#__PURE__*/React.createElement("svg", _extends({
    width: 20,
    height: 21,
    viewBox: "0 0 20 21",
    fill: "none"
  }, props), /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M15.833 3c.917 0 1.667.75 1.667 1.667v11.666c0 .917-.75 1.667-1.667 1.667H4.167C3.25 18 2.5 17.25 2.5 16.333V4.667C2.5 3.75 3.25 3 4.167 3h11.666zM9.167 14.258L7.083 11.75 4.167 15.5h11.666l-3.75-5-2.916 3.758z",
    fill: "#000"
  }));
};
var HorizontalRuleIcon = function HorizontalRuleIcon(props) {
  return /*#__PURE__*/React.createElement("svg", _extends({
    width: 20,
    height: 21,
    viewBox: "0 0 20 21",
    fill: "none"
  }, props), /*#__PURE__*/React.createElement("path", {
    d: "M15.833 11.333H4.167V9.667h11.666v1.666z",
    fill: "#000"
  }));
};
var MarkdownIcon = function MarkdownIcon(props) {
  return /*#__PURE__*/React.createElement("svg", _extends({
    width: 20,
    height: 21,
    viewBox: "0 0 20 21",
    fill: "none"
  }, props), /*#__PURE__*/React.createElement("path", {
    d: "M17.13 5.083H2.87c-.664 0-1.203.54-1.203 1.2v7.852c0 .665.539 1.204 1.203 1.204h14.263a1.2 1.2 0 001.2-1.201V6.284c0-.662-.539-1.2-1.203-1.2zm-6.648 7.852H8.88V9.81L7.28 11.812 5.677 9.81v3.125H4.07V7.487h1.602L7.273 9.49l1.602-2.003h1.602v5.448h.005zm3.523.08l-2.403-2.804h1.601V7.487h1.602v2.724h1.601l-2.4 2.805z",
    fill: "#000"
  }));
};

var isMarkActive = function isMarkActive(state, type) {
  var _state$selection = state.selection,
    from = _state$selection.from,
    $from = _state$selection.$from,
    to = _state$selection.to,
    empty = _state$selection.empty;
  if (empty) {
    return type.isInSet(state.storedMarks || $from.marks());
  } else {
    return state.doc.rangeHasMark(from, to, type);
  }
};
var canInsert = function canInsert(state, nodeType) {
  var $from = state.selection.$from;
  for (var d = $from.depth; d >= 0; d--) {
    var index = $from.index(d);
    var node = $from.node(d);
    if (node.canReplaceWith(index, index, nodeType)) {
      return true;
    }
  }
  return false;
};

var IconButton = _styled("button", {
  target: "epgl2co0",
  label: "IconButton"
})("display:block;border:none;background:transparent;margin:0 5px 0 0;padding:0;opacity:", function (props) {
  return props.active ? 0.8 : 0.5;
}, ";transition:opacity 200ms;cursor:pointer;&:hover{opacity:1;}&[disabled]{opacity:0.1;cursor:default;}svg{display:block;}" + (process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJzb3VyY2VzIjpbImljb24tYnV0dG9uLmpzIl0sIm5hbWVzIjpbXSwibWFwcGluZ3MiOiJBQUV1QyIsImZpbGUiOiJpY29uLWJ1dHRvbi5qcyIsInNvdXJjZXNDb250ZW50IjpbImltcG9ydCBzdHlsZWQgZnJvbSBcIkBlbW90aW9uL3N0eWxlZFwiO1xuXG5leHBvcnQgY29uc3QgSWNvbkJ1dHRvbiA9IHN0eWxlZC5idXR0b25gXG4gIGRpc3BsYXk6IGJsb2NrO1xuICBib3JkZXI6IG5vbmU7XG4gIGJhY2tncm91bmQ6IHRyYW5zcGFyZW50O1xuICBtYXJnaW46IDAgNXB4IDAgMDtcbiAgcGFkZGluZzogMDtcbiAgb3BhY2l0eTogJHtwcm9wcyA9PiAocHJvcHMuYWN0aXZlID8gMC44IDogMC41KX07XG4gIHRyYW5zaXRpb246IG9wYWNpdHkgMjAwbXM7XG4gIGN1cnNvcjogcG9pbnRlcjtcblxuICAmOmhvdmVyIHtcbiAgICBvcGFjaXR5OiAxO1xuICB9XG5cbiAgJltkaXNhYmxlZF0ge1xuICAgIG9wYWNpdHk6IDAuMTtcbiAgICBjdXJzb3I6IGRlZmF1bHQ7XG4gIH1cblxuICBzdmcge1xuICAgIGRpc3BsYXk6IGJsb2NrO1xuICB9XG5gO1xuIl19 */"));

var RichTextButton = function RichTextButton(props) {
  var active = false,
    disabled = true,
    onClick;
  if (props.state != null) {
    active = props.active ? props.active(props.state) : false;
    disabled = props.disabled ? props.disabled(props.state) : false;
    onClick = props.onClick ? function () {
      return props.onClick(props.state, props.view.current);
    } : undefined;
  }
  var Icon = props.icon;
  return /*#__PURE__*/React.createElement(IconButton, {
    active: active,
    disabled: disabled,
    onClick: onClick,
    title: props.title,
    type: "button"
  }, /*#__PURE__*/React.createElement(Icon, null));
};

var ToggleMarkButton = function ToggleMarkButton(props) {
  var cmd = toggleMark(props.mark);
  return /*#__PURE__*/React.createElement(RichTextButton, {
    icon: props.icon,
    title: props.title,
    state: props.state,
    view: props.view,
    active: function active(state) {
      return isMarkActive(state, props.mark);
    },
    disabled: function disabled(state) {
      return !cmd(state, null);
    },
    onClick: function onClick(state, view) {
      return cmd(state, view.dispatch);
    }
  });
};

function _EMOTION_STRINGIFIED_CSS_ERROR__$7() { return "You have tried to stringify object returned from `css` function. It isn't supposed to be used directly (e.g. as value of the `className` prop), but rather handed to emotion so it can handle it (e.g. as value of `css` prop)."; }
var MARK_TYPE = schema.marks.link;
var LinkButton = function LinkButton(props) {
  var _React$useState = React.useState(false),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    modalOpen = _React$useState2[0],
    setModalOpen = _React$useState2[1];
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(RichTextButton, {
    icon: LinkIcon,
    title: "Insert link",
    state: props.state,
    view: props.view,
    active: function active(state) {
      return isMarkActive(state, MARK_TYPE);
    },
    disabled: function disabled(state) {
      return state.selection.empty;
    },
    onClick: function onClick(state, view) {
      if (isMarkActive(state, MARK_TYPE)) {
        toggleMark(MARK_TYPE)(state, view.dispatch);
      } else {
        setModalOpen(true);
      }
    }
  }), /*#__PURE__*/React.createElement(UrlModal, {
    open: modalOpen,
    onSave: function onSave(attrs) {
      toggleMark(MARK_TYPE, attrs)(props.state, props.view.current.dispatch);
      setModalOpen(false);
    },
    onClose: function onClose() {
      return setModalOpen(false);
    }
  }));
};
var lastId = 1;
var UrlModal = function UrlModal(props) {
  var inputId = React.useMemo(function () {
    return "toggl-hire-editor-url-".concat(lastId++);
  }, []);
  var form = useFormik({
    initialValues: {
      href: ""
    },
    onSubmit: function onSubmit(values) {
      return props.onSave(values);
    },
    validationSchema: validationSchema
  });
  React.useEffect(function () {
    if (!props.open) {
      form.resetForm();
    }
  }, [props.open]);
  return /*#__PURE__*/React.createElement(Modal, {
    size: "small",
    layer: 2,
    open: props.open,
    onClose: props.onClose
  }, /*#__PURE__*/React.createElement(Form, {
    onSubmit: form.handleSubmit,
    onReset: form.handleReset
  }, /*#__PURE__*/React.createElement(Field, {
    label: "URL:",
    for: inputId,
    error: form.touched.href && form.errors.href,
    input: /*#__PURE__*/React.createElement(TextInput, {
      name: "href",
      type: "text",
      id: inputId,
      onChange: form.handleChange,
      onBlur: form.handleBlur,
      value: form.values.href,
      error: form.touched.href && !!form.errors.href
    })
  }), /*#__PURE__*/React.createElement(Button, {
    type: "submit"
  }, "Save"), /*#__PURE__*/React.createElement(CloseButton, {
    type: "button",
    onClick: props.onClose
  })));
};
var Form = _styled("form", {
  target: "e1f9t5bg0",
  label: "Form"
})(process.env.NODE_ENV === "production" ? {
  name: "1rdca32",
  styles: "padding:20px;position:relative;"
} : {
  name: "1rdca32",
  styles: "padding:20px;position:relative;",
  map: "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */",
  toString: _EMOTION_STRINGIFIED_CSS_ERROR__$7
});
var Field = /*#__PURE__*/_styled(Field$1, {
  target: "e1f9t5bg1",
  label: "Field"
})(process.env.NODE_ENV === "production" ? {
  name: "uewl2b",
  styles: "margin-bottom:20px;"
} : {
  name: "uewl2b",
  styles: "margin-bottom:20px;",
  map: "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */",
  toString: _EMOTION_STRINGIFIED_CSS_ERROR__$7
});
var CloseButton = /*#__PURE__*/_styled(CloseModalButton, {
  target: "e1f9t5bg2",
  label: "CloseButton"
})(process.env.NODE_ENV === "production" ? {
  name: "1opzxad",
  styles: "position:absolute;top:10px;right:10px;"
} : {
  name: "1opzxad",
  styles: "position:absolute;top:10px;right:10px;",
  map: "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */",
  toString: _EMOTION_STRINGIFIED_CSS_ERROR__$7
});
var validationSchema = yup.object().shape({
  href: yup.string().required("Please enter the URL").url("Please enter a valid URL")
});

var UndoButton = function UndoButton(props) {
  return /*#__PURE__*/React.createElement(RichTextButton, {
    icon: UndoIcon,
    title: "Undo last action",
    state: props.state,
    view: props.view,
    disabled: function disabled(state) {
      return !undo(state);
    },
    onClick: function onClick(state, view) {
      undo(state, view.dispatch);
    }
  });
};
var RedoButton = function RedoButton(props) {
  return /*#__PURE__*/React.createElement(RichTextButton, {
    icon: RedoIcon,
    title: "Repeat last undone action",
    state: props.state,
    view: props.view,
    disabled: function disabled(state) {
      return !redo(state);
    },
    onClick: function onClick(state, view) {
      redo(state, view.dispatch);
    }
  });
};

var ListButton = function ListButton(props) {
  return /*#__PURE__*/React.createElement(RichTextButton, {
    icon: props.icon,
    title: props.title,
    state: props.state,
    view: props.view,
    disabled: function disabled(state) {
      return !wrapInList(props.type)(state);
    },
    onClick: function onClick(state, view) {
      wrapInList(props.type)(state, view.dispatch);
    }
  });
};
var BulletListButton = function BulletListButton(props) {
  return /*#__PURE__*/React.createElement(ListButton, _extends({
    type: schema.nodes.bullet_list,
    icon: BulletListIcon,
    title: "Insert bullet list"
  }, props));
};
var NumberListButton = function NumberListButton(props) {
  return /*#__PURE__*/React.createElement(ListButton, _extends({
    type: schema.nodes.ordered_list,
    icon: NumberListIcon,
    title: "Insert numbered list"
  }, props));
};

var QuoteButton = function QuoteButton(props) {
  return /*#__PURE__*/React.createElement(RichTextButton, {
    icon: QuoteIcon,
    title: "Insert quote",
    state: props.state,
    view: props.view,
    disabled: function disabled(state) {
      return !wrapIn(schema.nodes.blockquote)(state);
    },
    onClick: function onClick(state, view) {
      wrapIn(schema.nodes.blockquote)(state, view.dispatch);
    }
  });
};

var _excluded$2 = ["className", "style"];
function _EMOTION_STRINGIFIED_CSS_ERROR__$6() { return "You have tried to stringify object returned from `css` function. It isn't supposed to be used directly (e.g. as value of the `className` prop), but rather handed to emotion so it can handle it (e.g. as value of `css` prop)."; }
var Container$3 = _styled("div", {
  target: "e1jowfmo0",
  label: "Container"
})(process.env.NODE_ENV === "production" ? {
  name: "jo58tu",
  styles: "position:relative;&::after{content:\"\";display:block;position:absolute;top:50%;right:10px;transform:translateY(-50%);border-style:solid;border-width:4px 4px 0 4px;border-color:#e0e0e0 transparent transparent;}"
} : {
  name: "jo58tu",
  styles: "position:relative;&::after{content:\"\";display:block;position:absolute;top:50%;right:10px;transform:translateY(-50%);border-style:solid;border-width:4px 4px 0 4px;border-color:#e0e0e0 transparent transparent;}",
  map: "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJzb3VyY2VzIjpbInNlbGVjdC5qcyJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiQUFHNEIiLCJmaWxlIjoic2VsZWN0LmpzIiwic291cmNlc0NvbnRlbnQiOlsiaW1wb3J0IFJlYWN0IGZyb20gXCJyZWFjdFwiO1xuaW1wb3J0IHN0eWxlZCBmcm9tIFwiQGVtb3Rpb24vc3R5bGVkXCI7XG5cbmNvbnN0IENvbnRhaW5lciA9IHN0eWxlZC5kaXZgXG4gIHBvc2l0aW9uOiByZWxhdGl2ZTtcblxuICAmOjphZnRlciB7XG4gICAgY29udGVudDogXCJcIjtcbiAgICBkaXNwbGF5OiBibG9jaztcbiAgICBwb3NpdGlvbjogYWJzb2x1dGU7XG4gICAgdG9wOiA1MCU7XG4gICAgcmlnaHQ6IDEwcHg7XG4gICAgdHJhbnNmb3JtOiB0cmFuc2xhdGVZKC01MCUpO1xuICAgIGJvcmRlci1zdHlsZTogc29saWQ7XG4gICAgYm9yZGVyLXdpZHRoOiA0cHggNHB4IDAgNHB4O1xuICAgIGJvcmRlci1jb2xvcjogI2UwZTBlMCB0cmFuc3BhcmVudCB0cmFuc3BhcmVudDtcbiAgfVxuYDtcblxuY29uc3QgQ29udHJvbCA9IHN0eWxlZC5zZWxlY3RgXG4gIGRpc3BsYXk6IGJsb2NrO1xuICB3aWR0aDogMTAwJTtcbiAgcGFkZGluZzogNHB4IDZweDtcbiAgZm9udC1zaXplOiAxMHB4O1xuICBjb2xvcjogIzMzMzMzMztcbiAgbGluZS1oZWlnaHQ6IDEuNTtcbiAgYmFja2dyb3VuZDogI2ZmZjtcbiAgYm9yZGVyOiAxcHggc29saWQgI2UwZTBlMDtcbiAgYm9yZGVyLXJhZGl1czogMDtcbiAgdHJhbnNpdGlvbjogYm9yZGVyLWNvbG9yIDAuMnMsIGJhY2tncm91bmQtY29sb3IgMC4ycztcbiAgLXdlYmtpdC1hcHBlYXJhbmNlOiBub25lO1xuICAtbW96LWFwcGVhcmFuY2U6IG5vbmU7XG5cbiAgJjpmb2N1cyB7XG4gICAgb3V0bGluZTogbm9uZTtcbiAgICBib3JkZXItY29sb3I6ICMzMzMzMzM7XG4gIH1cblxuICAmOjotbXMtZXhwYW5kIHtcbiAgICBkaXNwbGF5OiBub25lO1xuICB9XG5gO1xuXG5leHBvcnQgY29uc3QgU2VsZWN0ID0gcHJvcHMgPT4ge1xuICBjb25zdCB7IGNsYXNzTmFtZSwgc3R5bGUsIC4uLnJlc3QgfSA9IHByb3BzO1xuXG4gIHJldHVybiAoXG4gICAgPENvbnRhaW5lciBjbGFzc05hbWU9e2NsYXNzTmFtZX0gc3R5bGU9e3N0eWxlfT5cbiAgICAgIDxDb250cm9sIHsuLi5yZXN0fSAvPlxuICAgIDwvQ29udGFpbmVyPlxuICApO1xufTtcbiJdfQ== */",
  toString: _EMOTION_STRINGIFIED_CSS_ERROR__$6
});
var Control = _styled("select", {
  target: "e1jowfmo1",
  label: "Control"
})(process.env.NODE_ENV === "production" ? {
  name: "1cru31x",
  styles: "display:block;width:100%;padding:4px 6px;font-size:10px;color:#333333;line-height:1.5;background:#fff;border:1px solid #e0e0e0;border-radius:0;transition:border-color 0.2s,background-color 0.2s;-webkit-appearance:none;-moz-appearance:none;&:focus{outline:none;border-color:#333333;}&::-ms-expand{display:none;}"
} : {
  name: "1cru31x",
  styles: "display:block;width:100%;padding:4px 6px;font-size:10px;color:#333333;line-height:1.5;background:#fff;border:1px solid #e0e0e0;border-radius:0;transition:border-color 0.2s,background-color 0.2s;-webkit-appearance:none;-moz-appearance:none;&:focus{outline:none;border-color:#333333;}&::-ms-expand{display:none;}",
  map: "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJzb3VyY2VzIjpbInNlbGVjdC5qcyJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiQUFtQjZCIiwiZmlsZSI6InNlbGVjdC5qcyIsInNvdXJjZXNDb250ZW50IjpbImltcG9ydCBSZWFjdCBmcm9tIFwicmVhY3RcIjtcbmltcG9ydCBzdHlsZWQgZnJvbSBcIkBlbW90aW9uL3N0eWxlZFwiO1xuXG5jb25zdCBDb250YWluZXIgPSBzdHlsZWQuZGl2YFxuICBwb3NpdGlvbjogcmVsYXRpdmU7XG5cbiAgJjo6YWZ0ZXIge1xuICAgIGNvbnRlbnQ6IFwiXCI7XG4gICAgZGlzcGxheTogYmxvY2s7XG4gICAgcG9zaXRpb246IGFic29sdXRlO1xuICAgIHRvcDogNTAlO1xuICAgIHJpZ2h0OiAxMHB4O1xuICAgIHRyYW5zZm9ybTogdHJhbnNsYXRlWSgtNTAlKTtcbiAgICBib3JkZXItc3R5bGU6IHNvbGlkO1xuICAgIGJvcmRlci13aWR0aDogNHB4IDRweCAwIDRweDtcbiAgICBib3JkZXItY29sb3I6ICNlMGUwZTAgdHJhbnNwYXJlbnQgdHJhbnNwYXJlbnQ7XG4gIH1cbmA7XG5cbmNvbnN0IENvbnRyb2wgPSBzdHlsZWQuc2VsZWN0YFxuICBkaXNwbGF5OiBibG9jaztcbiAgd2lkdGg6IDEwMCU7XG4gIHBhZGRpbmc6IDRweCA2cHg7XG4gIGZvbnQtc2l6ZTogMTBweDtcbiAgY29sb3I6ICMzMzMzMzM7XG4gIGxpbmUtaGVpZ2h0OiAxLjU7XG4gIGJhY2tncm91bmQ6ICNmZmY7XG4gIGJvcmRlcjogMXB4IHNvbGlkICNlMGUwZTA7XG4gIGJvcmRlci1yYWRpdXM6IDA7XG4gIHRyYW5zaXRpb246IGJvcmRlci1jb2xvciAwLjJzLCBiYWNrZ3JvdW5kLWNvbG9yIDAuMnM7XG4gIC13ZWJraXQtYXBwZWFyYW5jZTogbm9uZTtcbiAgLW1vei1hcHBlYXJhbmNlOiBub25lO1xuXG4gICY6Zm9jdXMge1xuICAgIG91dGxpbmU6IG5vbmU7XG4gICAgYm9yZGVyLWNvbG9yOiAjMzMzMzMzO1xuICB9XG5cbiAgJjo6LW1zLWV4cGFuZCB7XG4gICAgZGlzcGxheTogbm9uZTtcbiAgfVxuYDtcblxuZXhwb3J0IGNvbnN0IFNlbGVjdCA9IHByb3BzID0+IHtcbiAgY29uc3QgeyBjbGFzc05hbWUsIHN0eWxlLCAuLi5yZXN0IH0gPSBwcm9wcztcblxuICByZXR1cm4gKFxuICAgIDxDb250YWluZXIgY2xhc3NOYW1lPXtjbGFzc05hbWV9IHN0eWxlPXtzdHlsZX0+XG4gICAgICA8Q29udHJvbCB7Li4ucmVzdH0gLz5cbiAgICA8L0NvbnRhaW5lcj5cbiAgKTtcbn07XG4iXX0= */",
  toString: _EMOTION_STRINGIFIED_CSS_ERROR__$6
});
var Select = function Select(props) {
  var className = props.className,
    style = props.style,
    rest = _objectWithoutProperties(props, _excluded$2);
  return /*#__PURE__*/React.createElement(Container$3, {
    className: className,
    style: style
  }, /*#__PURE__*/React.createElement(Control, rest));
};

function _EMOTION_STRINGIFIED_CSS_ERROR__$5() { return "You have tried to stringify object returned from `css` function. It isn't supposed to be used directly (e.g. as value of the `className` prop), but rather handed to emotion so it can handle it (e.g. as value of `css` prop)."; }
var BlockTypeSelect = function BlockTypeSelect(props) {
  if (props.state != null) {
    return /*#__PURE__*/React.createElement(RichTextSelect, {
      state: props.state,
      view: props.view
    });
  } else {
    return /*#__PURE__*/React.createElement(TypeSelect, {
      disabled: true
    });
  }
};
var RichTextSelect = function RichTextSelect(props) {
  var value = getCurrentValue(props.state);
  return /*#__PURE__*/React.createElement(TypeSelect, {
    title: "Block type",
    value: value,
    onChange: function onChange(event) {
      var option = getSelectedOption(event.target.value);
      if (option == null) return;
      setBlockType(option.node, option.attrs)(props.state, props.view.current.dispatch);
    }
  }, /*#__PURE__*/React.createElement("option", {
    value: "",
    disabled: true
  }), options.map(function (option) {
    return /*#__PURE__*/React.createElement(Option, {
      key: option.id,
      option: option,
      state: props.state
    });
  }));
};
var options = [{
  id: "paragraph",
  label: "Paragraph",
  node: schema.nodes.paragraph
}, {
  id: "code",
  label: "Code",
  node: schema.nodes.code_block
}];
for (var level = 1; level <= 6; level++) {
  options.push({
    id: "heading_".concat(level),
    label: "Heading ".concat(level),
    node: schema.nodes.heading,
    attrs: {
      level: level
    }
  });
}
var getCurrentValue = function getCurrentValue(state) {
  for (var _i = 0, _options = options; _i < _options.length; _i++) {
    var option = _options[_i];
    if (isBlockActive(state, option.node, option.attrs)) {
      return option.id;
    }
  }
  return "";
};
var isBlockActive = function isBlockActive(state, type, attrs) {
  var _state$selection = state.selection,
    $from = _state$selection.$from;
    _state$selection.to;
    var node = _state$selection.node;
  if (node) {
    return node.hasMarkup(type, attrs);
  } else {
    return $from.parent.hasMarkup(type, attrs);
  }
};
var getSelectedOption = function getSelectedOption(value) {
  for (var _i2 = 0, _options2 = options; _i2 < _options2.length; _i2++) {
    var option = _options2[_i2];
    if (option.id === value) {
      return option;
    }
  }
};
var TypeSelect = /*#__PURE__*/_styled(Select, {
  target: "e109ablj0",
  label: "TypeSelect"
})(process.env.NODE_ENV === "production" ? {
  name: "1158t33",
  styles: "width:100px;margin-right:5px;"
} : {
  name: "1158t33",
  styles: "width:100px;margin-right:5px;",
  map: "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */",
  toString: _EMOTION_STRINGIFIED_CSS_ERROR__$5
});
var Option = function Option(props) {
  var command = setBlockType(props.option.node, props.option.attrs);
  var disabled = !command(props.state);
  return /*#__PURE__*/React.createElement("option", {
    value: props.option.id,
    disabled: disabled
  }, props.option.label);
};

function _createForOfIteratorHelper(o, allowArrayLike) { var it = typeof Symbol !== "undefined" && o[Symbol.iterator] || o["@@iterator"]; if (!it) { if (Array.isArray(o) || (it = _unsupportedIterableToArray(o)) || allowArrayLike  ) { if (it) o = it; var i = 0; var F = function F() {}; return { s: F, n: function n() { if (i >= o.length) return { done: true }; return { done: false, value: o[i++] }; }, e: function e(_e) { throw _e; }, f: F }; } throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); } var normalCompletion = true, didErr = false, err; return { s: function s() { it = it.call(o); }, n: function n() { var step = it.next(); normalCompletion = step.done; return step; }, e: function e(_e2) { didErr = true; err = _e2; }, f: function f() { try { if (!normalCompletion && it.return != null) it.return(); } finally { if (didErr) throw err; } } }; }
function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }
function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i]; return arr2; }
function _EMOTION_STRINGIFIED_CSS_ERROR__$4() { return "You have tried to stringify object returned from `css` function. It isn't supposed to be used directly (e.g. as value of the `className` prop), but rather handed to emotion so it can handle it (e.g. as value of `css` prop)."; }
var key = new PluginKey("images");
var images = function images(uploadImage) {
  return new Plugin({
    key: key,
    state: {
      init: function init() {
        return {
          uploads: [],
          decorations: DecorationSet.empty,
          nextId: 1
        };
      },
      apply: function apply(tr, _ref) {
        var uploads = _ref.uploads,
          decorations = _ref.decorations,
          nextId = _ref.nextId;
        // Adjust decoration positions to changes made by the transaction
        decorations = decorations.map(tr.mapping, tr.doc);

        // See if the transaction adds or removes any placeholders
        var action = tr.getMeta(key);
        if (action) {
          if (action.add) {
            var _action$add = action.add,
              files = _action$add.files,
              position = _action$add.position;
            var _iterator = _createForOfIteratorHelper(files),
              _step;
            try {
              for (_iterator.s(); !(_step = _iterator.n()).done;) {
                var file = _step.value;
                var id = "image-".concat(nextId++);
                var widget = createWidget$1();
                var decoration = Decoration.widget(position, widget, {
                  id: id
                });
                decorations = decorations.add(tr.doc, [decoration]);
                uploads = [].concat(_toConsumableArray(uploads), [{
                  id: id,
                  file: file
                }]);
              }
            } catch (err) {
              _iterator.e(err);
            } finally {
              _iterator.f();
            }
          }
          if (action.start) {
            var _id = action.start.id;
            uploads = uploads.filter(function (upload) {
              return upload.id !== _id;
            });
          }
          if (action.finish) {
            var _id2 = action.finish.id;
            decorations = decorations.remove(decorations.find(null, null, function (spec) {
              return spec.id === _id2;
            }));
          }
        }
        return {
          uploads: uploads,
          decorations: decorations,
          nextId: nextId
        };
      }
    },
    view: function view() {
      return {
        update: function update(view) {
          var _key$getState = key.getState(view.state),
            uploads = _key$getState.uploads;
          var _uploads = _slicedToArray(uploads, 1),
            upload = _uploads[0];
          if (upload) {
            processUpload(view, upload.id, upload.file, uploadImage);
          }
        }
      };
    },
    props: {
      decorations: function decorations(state) {
        return key.getState(state).decorations;
      },
      handleDOMEvents: {
        drop: function drop(view, event) {
          var files = event.dataTransfer.files;
          if (files.length === 0) return false;
          event.preventDefault();
          var position = view.posAtCoords({
            left: event.clientX,
            top: event.clientY
          });
          if (position == null) return;
          addImages(view, files, position.pos);
        }
      }
    }
  });
};
var createWidget$1 = function createWidget() {
  var style = /*#__PURE__*/css(process.env.NODE_ENV === "production" ? {
    name: "92rsgb-style",
    styles: "display:inline-flex;justify-content:center;align-items:center;width:150px;height:100px;vertical-align:bottom;border:1px dashed #808080;font-size:12px;color:#808080;;label:style;"
  } : {
    name: "92rsgb-style",
    styles: "display:inline-flex;justify-content:center;align-items:center;width:150px;height:100px;vertical-align:bottom;border:1px dashed #808080;font-size:12px;color:#808080;;label:style;",
    map: "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */",
    toString: _EMOTION_STRINGIFIED_CSS_ERROR__$4
  });
  var element = document.createElement("span");
  element.className = style;
  element.textContent = "Uploading...";
  return element;
};
var processUpload = /*#__PURE__*/function () {
  var _ref2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(view, id, file, uploadImage) {
    var url, position, image;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) switch (_context.prev = _context.next) {
        case 0:
          // remove upload from the queue
          view.dispatch(view.state.tr.setMeta(key, {
            start: {
              id: id
            }
          }));
          _context.prev = 1;
          _context.next = 4;
          return uploadImage(file);
        case 4:
          url = _context.sent;
          _context.next = 11;
          break;
        case 7:
          _context.prev = 7;
          _context.t0 = _context["catch"](1);
          // remove decoration
          setTimeout(function () {
            view.dispatch(view.state.tr.setMeta(key, {
              finish: {
                id: id
              }
            }));
          }, 0);
          return _context.abrupt("return");
        case 11:
          // replace decoration with the uploaded image
          position = findImagePosition(view.state, id);
          if (!(position == null)) {
            _context.next = 14;
            break;
          }
          return _context.abrupt("return");
        case 14:
          image = schema.nodes.image.create({
            src: url
          });
          setTimeout(function () {
            view.dispatch(view.state.tr.insert(position, image).setMeta(key, {
              finish: {
                id: id
              }
            }));
          }, 0);
        case 16:
        case "end":
          return _context.stop();
      }
    }, _callee, null, [[1, 7]]);
  }));
  return function processUpload(_x, _x2, _x3, _x4) {
    return _ref2.apply(this, arguments);
  };
}();
var findImagePosition = function findImagePosition(state, id) {
  var _key$getState2 = key.getState(state),
    decorations = _key$getState2.decorations;
  var found = decorations.find(null, null, function (spec) {
    return spec.id === id;
  });
  return found.length > 0 ? found[0].from : null;
};
var addImages = function addImages(view, files, position) {
  view.dispatch(view.state.tr.setMeta(key, {
    add: {
      files: files,
      position: position
    }
  }));
};

var ImageButton = function ImageButton(props) {
  return /*#__PURE__*/React.createElement(RichTextButton, {
    icon: ImageIcon,
    title: "Insert image",
    state: props.state,
    view: props.view,
    disabled: function disabled(state) {
      return !canInsert(state, schema.nodes.image);
    },
    onClick: /*#__PURE__*/function () {
      var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(state, view) {
        var files, position;
        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) switch (_context.prev = _context.next) {
            case 0:
              _context.next = 2;
              return selectFiles({
                accept: "image/*",
                multiple: true
              });
            case 2:
              files = _context.sent;
              if (!(files == null || files.length === 0)) {
                _context.next = 5;
                break;
              }
              return _context.abrupt("return");
            case 5:
              position = state.selection.from;
              addImages(view, files, position);
            case 7:
            case "end":
              return _context.stop();
          }
        }, _callee);
      }));
      return function (_x, _x2) {
        return _ref.apply(this, arguments);
      };
    }()
  });
};

var HorizontalRuleButton = function HorizontalRuleButton(props) {
  return /*#__PURE__*/React.createElement(RichTextButton, {
    icon: HorizontalRuleIcon,
    title: "Insert horizontal divider",
    state: props.state,
    view: props.view,
    disabled: function disabled(state) {
      return !insertHorizontalRule(state);
    },
    onClick: function onClick(state, view) {
      insertHorizontalRule(state, view.dispatch);
    }
  });
};
var insertHorizontalRule = function insertHorizontalRule(state, dispatch) {
  if (!canInsert(state, schema.nodes.horizontal_rule)) return false;
  var tr = state.tr.replaceSelectionWith(schema.nodes.horizontal_rule.create());
  if (dispatch) dispatch(tr);
  return true;
};

var ToggleMarkdownButton = function ToggleMarkdownButton(props) {
  var active = props.input === "markdown";
  return /*#__PURE__*/React.createElement(IconButton, {
    title: "Edit Markdown",
    active: active,
    type: "button",
    onClick: function onClick() {
      props.onSwitchInput(active ? "rich-text" : "markdown");
    }
  }, /*#__PURE__*/React.createElement(MarkdownIcon, null));
};

function _EMOTION_STRINGIFIED_CSS_ERROR__$3() { return "You have tried to stringify object returned from `css` function. It isn't supposed to be used directly (e.g. as value of the `className` prop), but rather handed to emotion so it can handle it (e.g. as value of `css` prop)."; }
var Toolbar = function Toolbar(props) {
  return /*#__PURE__*/React.createElement(Container$2, null, /*#__PURE__*/React.createElement(BlockTypeSelect, {
    view: props.view,
    state: props.state
  }), /*#__PURE__*/React.createElement(ToggleMarkButton, {
    mark: schema.marks.strong,
    view: props.view,
    state: props.state,
    icon: StrongIcon,
    title: "Toggle strong"
  }), /*#__PURE__*/React.createElement(ToggleMarkButton, {
    mark: schema.marks.em,
    view: props.view,
    state: props.state,
    icon: EmphasisIcon,
    title: "Toggle emphasis"
  }), /*#__PURE__*/React.createElement(ToggleMarkButton, {
    mark: schema.marks.code,
    view: props.view,
    state: props.state,
    icon: InlineCodeIcon,
    title: "Toggle code"
  }), /*#__PURE__*/React.createElement(LinkButton, {
    view: props.view,
    state: props.state
  }), /*#__PURE__*/React.createElement(Divider, null), /*#__PURE__*/React.createElement(UndoButton, {
    view: props.view,
    state: props.state
  }), /*#__PURE__*/React.createElement(RedoButton, {
    view: props.view,
    state: props.state
  }), /*#__PURE__*/React.createElement(Divider, null), /*#__PURE__*/React.createElement(BulletListButton, {
    view: props.view,
    state: props.state
  }), /*#__PURE__*/React.createElement(NumberListButton, {
    view: props.view,
    state: props.state
  }), /*#__PURE__*/React.createElement(QuoteButton, {
    view: props.view,
    state: props.state
  }), /*#__PURE__*/React.createElement(Divider, null), /*#__PURE__*/React.createElement(ImageButton, {
    view: props.view,
    state: props.state
  }), /*#__PURE__*/React.createElement(HorizontalRuleButton, {
    view: props.view,
    state: props.state
  }), /*#__PURE__*/React.createElement(Divider, null), /*#__PURE__*/React.createElement(ToggleMarkdownButton, {
    input: props.input,
    onSwitchInput: props.onSwitchInput
  }));
};
var Container$2 = _styled("div", {
  target: "e1a2ydio0",
  label: "Container"
})(process.env.NODE_ENV === "production" ? {
  name: "ajikt8",
  styles: "flex-shrink:0;display:flex;flex-direction:row;align-items:center;"
} : {
  name: "ajikt8",
  styles: "flex-shrink:0;display:flex;flex-direction:row;align-items:center;",
  map: "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */",
  toString: _EMOTION_STRINGIFIED_CSS_ERROR__$3
});
var Divider = _styled("div", {
  target: "e1a2ydio1",
  label: "Divider"
})(process.env.NODE_ENV === "production" ? {
  name: "3x6w2z",
  styles: "width:1px;height:20px;margin:0 5px 0 0;background:#e7e7e7;flex-shrink:0;"
} : {
  name: "3x6w2z",
  styles: "width:1px;height:20px;margin:0 5px 0 0;background:#e7e7e7;flex-shrink:0;",
  map: "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */",
  toString: _EMOTION_STRINGIFIED_CSS_ERROR__$3
});

var blockQuote = wrappingInputRule(/^\s*>\s$/, schema.nodes.blockquote);
var orderedList = wrappingInputRule(/^(\d+)\.\s$/, schema.nodes.ordered_list);
var bulletList = wrappingInputRule(/^\s*([-+*])\s$/, schema.nodes.bullet_list);
var codeBlock = textblockTypeInputRule(/^```$/, schema.nodes.code_block);
var heading = textblockTypeInputRule(/^(#{1,6})\s$/, schema.nodes.heading, function (match) {
  return {
    level: match[1].length
  };
});
var templateVariableRule = new InputRule(/\[([^[]*)\]$/, function (state, match, start, end) {
  var tr = state.tr.delete(start, end);
  var node = schema.node('template_variable', {
    variable: match[1]
  }, []);
  tr.insert(start, node);
  return tr;
});
var rules = [].concat(_toConsumableArray(smartQuotes), [emDash, ellipsis, blockQuote, orderedList, bulletList, codeBlock, heading]);

var insertNewline = function insertNewline(state, dispatch) {
  var tr = state.tr.replaceSelectionWith(schema.nodes.hard_break.create()).scrollIntoView();
  if (dispatch) dispatch(tr);
  return true;
};

var editorKeymap = {
  "Mod-z": undo,
  "Shift-Mod-z": redo,
  "Mod-b": toggleMark(schema.marks.strong),
  "Mod-i": toggleMark(schema.marks.em),
  "Mod-`": toggleMark(schema.marks.code),
  "Mod-Enter": chainCommands(exitCode, insertNewline),
  "Shift-Enter": chainCommands(exitCode, insertNewline),
  Enter: splitListItem(schema.nodes.list_item),
  "Mod-[": liftListItem(schema.nodes.list_item),
  "Mod-]": sinkListItem(schema.nodes.list_item),
  Backspace: undoInputRule
};

function _EMOTION_STRINGIFIED_CSS_ERROR__$2() { return "You have tried to stringify object returned from `css` function. It isn't supposed to be used directly (e.g. as value of the `className` prop), but rather handed to emotion so it can handle it (e.g. as value of `css` prop)."; }
var isEmpty = function isEmpty(document) {
  return document.childCount === 1 && document.firstChild.type === schema.nodes.paragraph && document.firstChild.childCount === 0;
};
var placeholder = function placeholder(_placeholder) {
  return new Plugin({
    props: {
      decorations: function decorations(state) {
        if (isEmpty(state.doc)) {
          var widget = createWidget(_placeholder);
          var decoration = Decoration.widget(0, widget);
          return DecorationSet.create(state.doc, [decoration]);
        } else {
          return DecorationSet.empty;
        }
      }
    }
  });
};
var createWidget = function createWidget(placeholder) {
  var style = /*#__PURE__*/css(process.env.NODE_ENV === "production" ? {
    name: "t6n0pg-style",
    styles: "position:absolute;top:10px;left:0;pointer-events:none;user-select:none;color:#808080;;label:style;"
  } : {
    name: "t6n0pg-style",
    styles: "position:absolute;top:10px;left:0;pointer-events:none;user-select:none;color:#808080;;label:style;",
    map: "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJzb3VyY2VzIjpbInBsYWNlaG9sZGVyLmpzIl0sIm5hbWVzIjpbXSwibWFwcGluZ3MiOiJBQThCbUIiLCJmaWxlIjoicGxhY2Vob2xkZXIuanMiLCJzb3VyY2VzQ29udGVudCI6WyJpbXBvcnQgeyBQbHVnaW4gfSBmcm9tIFwicHJvc2VtaXJyb3Itc3RhdGVcIjtcbmltcG9ydCB7IHNjaGVtYSB9IGZyb20gXCIuLi9zY2hlbWFcIjtcbmltcG9ydCB7IERlY29yYXRpb24sIERlY29yYXRpb25TZXQgfSBmcm9tIFwicHJvc2VtaXJyb3Itdmlld1wiO1xuaW1wb3J0IHsgY3NzIH0gZnJvbSBcImVtb3Rpb25cIjtcblxuY29uc3QgaXNFbXB0eSA9IGRvY3VtZW50ID0+IHtcbiAgcmV0dXJuIChcbiAgICBkb2N1bWVudC5jaGlsZENvdW50ID09PSAxICYmXG4gICAgZG9jdW1lbnQuZmlyc3RDaGlsZC50eXBlID09PSBzY2hlbWEubm9kZXMucGFyYWdyYXBoICYmXG4gICAgZG9jdW1lbnQuZmlyc3RDaGlsZC5jaGlsZENvdW50ID09PSAwXG4gICk7XG59O1xuXG5leHBvcnQgY29uc3QgcGxhY2Vob2xkZXIgPSBwbGFjZWhvbGRlciA9PiB7XG4gIHJldHVybiBuZXcgUGx1Z2luKHtcbiAgICBwcm9wczoge1xuICAgICAgZGVjb3JhdGlvbnM6IHN0YXRlID0+IHtcbiAgICAgICAgaWYgKGlzRW1wdHkoc3RhdGUuZG9jKSkge1xuICAgICAgICAgIGNvbnN0IHdpZGdldCA9IGNyZWF0ZVdpZGdldChwbGFjZWhvbGRlcik7XG4gICAgICAgICAgY29uc3QgZGVjb3JhdGlvbiA9IERlY29yYXRpb24ud2lkZ2V0KDAsIHdpZGdldCk7XG4gICAgICAgICAgcmV0dXJuIERlY29yYXRpb25TZXQuY3JlYXRlKHN0YXRlLmRvYywgW2RlY29yYXRpb25dKTtcbiAgICAgICAgfSBlbHNlIHtcbiAgICAgICAgICByZXR1cm4gRGVjb3JhdGlvblNldC5lbXB0eTtcbiAgICAgICAgfVxuICAgICAgfVxuICAgIH1cbiAgfSk7XG59O1xuXG5jb25zdCBjcmVhdGVXaWRnZXQgPSBwbGFjZWhvbGRlciA9PiB7XG4gIGNvbnN0IHN0eWxlID0gY3NzYFxuICAgIHBvc2l0aW9uOiBhYnNvbHV0ZTtcbiAgICB0b3A6IDEwcHg7XG4gICAgbGVmdDogMDtcbiAgICBwb2ludGVyLWV2ZW50czogbm9uZTtcbiAgICB1c2VyLXNlbGVjdDogbm9uZTtcbiAgICBjb2xvcjogIzgwODA4MDtcbiAgYDtcblxuICBjb25zdCBlbGVtZW50ID0gZG9jdW1lbnQuY3JlYXRlRWxlbWVudChcImRpdlwiKTtcbiAgZWxlbWVudC50ZXh0Q29udGVudCA9IHBsYWNlaG9sZGVyO1xuICBlbGVtZW50LmNsYXNzTmFtZSA9IHN0eWxlO1xuXG4gIHJldHVybiBlbGVtZW50O1xufTtcbiJdfQ== */",
    toString: _EMOTION_STRINGIFIED_CSS_ERROR__$2
  });
  var element = document.createElement("div");
  element.textContent = placeholder;
  element.className = style;
  return element;
};

var getMentionSuggestionsHTML = function getMentionSuggestionsHTML(items) {
  return '<div class="suggestion-item-list">' + items.map(function (i) {
    return '<div class="suggestion-item">' + i.name + '</div>';
  }).join('') + '</div>';
};
var mentions = function mentions(users) {
  return getMentionsPlugin({
    getSuggestions: function getSuggestions(type, text, done) {
      if (type === 'mention') {
        done(users);
      }
    },
    getSuggestionsHTML: function getSuggestionsHTML(items, type) {
      if (type === 'mention') {
        return getMentionSuggestionsHTML(items);
      }
    },
    delay: 50,
    allowSpace: false,
    suggestionTextClass: "prosemirror-suggestion"
  });
};

var createPlugins = function createPlugins(options, users) {
  var plugins = [inputRules({
    rules: rules
  }), keymap(editorKeymap), keymap(baseKeymap), dropCursor(), gapCursor(), history(), images(options.uploadImage)];
  if (options.enableTemplateVariables) {
    plugins.push(inputRules({
      rules: [templateVariableRule]
    }));
  }
  if (options.placeholder) {
    plugins.push(placeholder(options.placeholder));
  }
  if (options.enableMentions) {
    plugins.push(mentions(users));
  }
  return plugins;
};

var getEditorClassName = function getEditorClassName(className) {
  return ["toggl-hire-editor", className].filter(function (x) {
    return x != null;
  }).join(" ");
};

function _EMOTION_STRINGIFIED_CSS_ERROR__$1() { return "You have tried to stringify object returned from `css` function. It isn't supposed to be used directly (e.g. as value of the `className` prop), but rather handed to emotion so it can handle it (e.g. as value of `css` prop)."; }
var RichTextEditor = function RichTextEditor(props) {
  // ensure uploadImage does not change even if the prop is updated
  var uploadImage = useRefCallback(props.uploadImage);

  // editor plugin options, updated only if the placeholder changes
  var options = React.useMemo(function () {
    return {
      uploadImage: uploadImage,
      placeholder: props.placeholder,
      enableMentions: props.enableMentions,
      enableTemplateVariables: props.enableTemplateVariables,
      enableSchedulingLink: props.enableSchedulingLink
    };
  }, [uploadImage, props.placeholder, props.enableMentions, props.enableTemplateVariables, props.enableSchedulingLink]);

  // initial editor state
  var _React$useState = React.useState(createState(props.document, options, props.users)),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    editorState = _React$useState2[0],
    setEditorState = _React$useState2[1];

  // if the document is updated from a parent component and it is different to
  // the document in the editor, update the editor
  React.useEffect(function () {
    if (props.document !== editorState.doc) {
      setEditorState(createState(props.document, options, props.users));
    }
  }, [props.document]);

  // track initial component mount
  var mounted = React.useRef(false);

  // if the options have changed after the component mounted, update the editor
  React.useEffect(function () {
    if (mounted.current) {
      setEditorState(updateState(editorState, options, props.users));
    }
  }, [options]);
  React.useEffect(function () {
    mounted.current = true;
  }, []);
  return /*#__PURE__*/React.createElement(Container$1, {
    className: getEditorClassName(props.className),
    style: props.style
  }, /*#__PURE__*/React.createElement(ProseMirror, {
    state: editorState,
    renderAboveEditorContent: props.renderAboveEditorContent,
    onChange: function onChange(state) {
      setEditorState(state);
      props.onChange(state.doc);
    },
    onSwitchInput: props.onSwitchInput,
    onFocus: props.onFocus,
    onBlur: props.onBlur,
    tabIndex: props.tabIndex,
    autoFocus: props.autoFocus,
    disabled: props.disabled,
    resizable: props.resizable
  }));
};
RichTextEditor.propTypes = {
  document: PropTypes.instanceOf(Node).isRequired,
  onChange: PropTypes.func.isRequired,
  onSwitchInput: PropTypes.func.isRequired,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  tabIndex: PropTypes.number,
  users: PropTypes.array,
  enableMentions: PropTypes.bool,
  enableTemplateVariables: PropTypes.bool,
  renderAboveEditorContent: PropTypes.func,
  autoFocus: PropTypes.bool,
  disabled: PropTypes.bool,
  resizable: PropTypes.bool,
  placeholder: PropTypes.string,
  uploadImage: PropTypes.func,
  className: PropTypes.string,
  style: PropTypes.object
};
var ProseMirror = function ProseMirror(props) {
  // store props that might change and are used by the editor
  var disabled = useRefValue(props.disabled);
  var handleFocus = useRefCallback(props.onFocus);
  var handleBlur = useRefCallback(props.onBlur);
  var handleChange = useRefCallback(props.onChange);

  // create the editor view on mount
  var editorView = React.useRef();
  var editorElement = React.useRef();
  React.useEffect(function () {
    var view = new EditorView({
      mount: editorElement.current
    }, {
      state: props.state,
      dispatchTransaction: function dispatchTransaction(transaction) {
        var next = view.state.apply(transaction);
        handleChange(next);
      },
      editable: function editable() {
        return !disabled.current;
      },
      handleDOMEvents: {
        focus: function focus(view, event) {
          return handleFocus(event);
        },
        blur: function blur(view, event) {
          return handleBlur(event);
        }
      }
    });
    editorView.current = view;
    if (props.autoFocus) {
      view.focus();
    }
    return function () {
      view.destroy();
    };
  }, []);

  // forward the updated state to the editor view
  React.useEffect(function () {
    if (props.state !== editorView.current.state) {
      editorView.current.updateState(props.state);
    }
  }, [props.state]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Toolbar, {
    view: editorView,
    state: !props.disabled ? props.state : null,
    onSwitchInput: props.onSwitchInput,
    input: "rich-text"
  }), props.renderAboveEditorContent && props.renderAboveEditorContent(editorView.current), /*#__PURE__*/React.createElement(EditorContent, {
    ref: editorElement,
    tabIndex: props.tabIndex,
    resizable: props.resizable
  }));
};
ProseMirror.propTypes = {
  state: PropTypes.instanceOf(EditorState).isRequired,
  renderAboveEditorContent: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  onSwitchInput: PropTypes.func.isRequired,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  tabIndex: PropTypes.number,
  autoFocus: PropTypes.bool,
  disabled: PropTypes.bool,
  resizable: PropTypes.bool
};
var useRefValue = function useRefValue(value) {
  var ref = React.useRef();
  React.useEffect(function () {
    ref.current = value;
  }, [value]);
  return ref;
};
var useRefCallback = function useRefCallback(fn) {
  var ref = useRefValue(fn);
  return React.useCallback(function () {
    if (ref.current != null) {
      for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
        args[_key] = arguments[_key];
      }
      return ref.current.apply(null, args);
    }
  }, []);
};
var createState = function createState(document, options, users) {
  return EditorState.create({
    doc: document,
    plugins: createPlugins(options, users)
  });
};
var updateState = function updateState(state, options, users) {
  return state.reconfigure({
    plugins: createPlugins(options, users)
  });
};
var Container$1 = _styled("div", {
  target: "espievc0",
  label: "Container"
})(process.env.NODE_ENV === "production" ? {
  name: "j7qwjs",
  styles: "display:flex;flex-direction:column;"
} : {
  name: "j7qwjs",
  styles: "display:flex;flex-direction:column;",
  map: "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */",
  toString: _EMOTION_STRINGIFIED_CSS_ERROR__$1
});
var EditorContent = /*#__PURE__*/_styled(Content$1, {
  target: "espievc1",
  label: "EditorContent"
})("flex-grow:1;margin-top:10px;resize:", function (props) {
  return props.resizable ? "vertical" : "";
}, ";overflow-y:", function (props) {
  return props.resizable ? "auto" : "";
}, ";&:focus{outline:none;}&.ProseMirror{position:relative;}&.ProseMirror{word-wrap:break-word;white-space:pre-wrap;white-space:break-spaces;-webkit-font-variant-ligatures:none;font-variant-ligatures:none;font-feature-settings:\"liga\" 0;}&.ProseMirror pre{white-space:pre-wrap;}&.ProseMirror li{position:relative;}&.ProseMirror .ProseMirror-hideselection *::selection{background:transparent;}&.ProseMirror .ProseMirror-hideselection *::-moz-selection{background:transparent;}&.ProseMirror .ProseMirror-hideselection{caret-color:transparent;}&.ProseMirror .ProseMirror-selectednode{outline:2px solid #8cf;}&.ProseMirror li.ProseMirror-selectednode{outline:none;}&.ProseMirror li.ProseMirror-selectednode:after{content:\"\";position:absolute;left:-32px;right:-2px;top:-2px;bottom:-2px;border:2px solid #8cf;pointer-events:none;}" + (process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */"));

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
var serializer = defaultMarkdownSerializer;
var markdownParser = new MarkdownParser(schema, defaultMarkdownParser.tokenizer, defaultMarkdownParser.tokens);
serializer.nodes = _objectSpread(_objectSpread({}, serializer.nodes), {}, {
  mention: function mention(state, node) {
    state.text("<span data-mention-id=\"".concat(node.attrs.id, "\" data-mention-name=\"").concat(node.attrs.name, "\" data-mention-email=\"").concat(node.attrs.email, "\" class=\"prosemirror-mention-node\" contenteditable=\"false\">@").concat(node.attrs.name, "</span>"));
  }
});
var RichTextState = /*#__PURE__*/function () {
  function RichTextState(document) {
    _classCallCheck(this, RichTextState);
    this.document = document;
  }
  _createClass(RichTextState, [{
    key: "markdown",
    value: function markdown() {
      return serializer.serialize(this.document);
    }
  }], [{
    key: "create",
    value: function create(markdown) {
      var document = markdownParser.parse(markdown);
      return new RichTextState(document);
    }
  }]);
  return RichTextState;
}();
var MarkdownState = /*#__PURE__*/function () {
  function MarkdownState(content) {
    _classCallCheck(this, MarkdownState);
    this.content = content;
  }
  _createClass(MarkdownState, [{
    key: "markdown",
    value: function markdown() {
      return this.content;
    }
  }], [{
    key: "create",
    value: function create(markdown) {
      return new MarkdownState(markdown);
    }
  }]);
  return MarkdownState;
}();
var editorStateToHtml = function editorStateToHtml(state) {
  var element = document.createElement('div');
  var fragment = DOMSerializer.fromSchema(schema).serializeFragment(state.document.content);
  element.appendChild(fragment);
  return element.innerHTML;
};
var htmlToEditorState = function htmlToEditorState(html) {
  var element = document.createElement('div');
  element.innerHTML = html.trim().replace(/\n/g, '<br />');
  var node = DOMParser.fromSchema(schema).parse(element);
  return new RichTextState(node);
};

var _excluded$1 = ["value", "onChange", "onSwitchInput", "className", "style"];
function _EMOTION_STRINGIFIED_CSS_ERROR__() { return "You have tried to stringify object returned from `css` function. It isn't supposed to be used directly (e.g. as value of the `className` prop), but rather handed to emotion so it can handle it (e.g. as value of `css` prop)."; }
var MarkdownEditor = function MarkdownEditor(props) {
  var value = props.value,
    _onChange = props.onChange,
    onSwitchInput = props.onSwitchInput,
    className = props.className,
    style = props.style,
    rest = _objectWithoutProperties(props, _excluded$1);
  var _React$useState = React.useState(),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    rowNumber = _React$useState2[0],
    setRowNumber = _React$useState2[1];
  var resizedByAutosize = React.useRef(false);
  var contentElement = React.useRef(null);
  React.useEffect(function () {
    var element = contentElement.current;
    function onResize() {
      if (resizedByAutosize.current) {
        resizedByAutosize.current = false;
        return;
      }
      setRowNumber(calculateRows(element));
    }
    var resizeObsever = new ResizeObserver(onResize);
    if (element instanceof Element) {
      resizeObsever.observe(element);
    }
    return function () {
      if (element instanceof Element) {
        resizeObsever.unobserve(element);
      }
    };
  }, [contentElement]);
  return /*#__PURE__*/React.createElement(Container, {
    className: getEditorClassName(className),
    style: style
  }, /*#__PURE__*/React.createElement(Toolbar, {
    view: null,
    state: null,
    input: "markdown",
    onSwitchInput: onSwitchInput
  }), /*#__PURE__*/React.createElement(Content, _extends({
    ref: contentElement,
    value: value,
    onChange: function onChange(e) {
      return _onChange(e.target.value);
    },
    onHeightChange: function onHeightChange() {
      resizedByAutosize.current = true;
    },
    minRows: rowNumber,
    maxRows: rowNumber
  }, rest)));
};
MarkdownEditor.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onSwitchInput: PropTypes.func.isRequired,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  tabIndex: PropTypes.number,
  autoFocus: PropTypes.bool,
  disabled: PropTypes.bool,
  resizable: PropTypes.bool,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  style: PropTypes.object
};
var Container = _styled("div", {
  target: "e1nf8fh30",
  label: "Container"
})(process.env.NODE_ENV === "production" ? {
  name: "j7qwjs",
  styles: "display:flex;flex-direction:column;"
} : {
  name: "j7qwjs",
  styles: "display:flex;flex-direction:column;",
  map: "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */",
  toString: _EMOTION_STRINGIFIED_CSS_ERROR__
});
var Content = /*#__PURE__*/_styled(TextareaAutosize, {
  target: "e1nf8fh31",
  label: "Content"
})("flex-grow:1;margin-top:20px;width:100%;border:none;background:transparent;resize:", function (props) {
  return props.resizable ? "vertical" : "none";
}, ";color:inherit;&:focus{outline:none;}" + (process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */"));
function calculateRows(el) {
  if (!el) return;
  var _getComputedStyle = getComputedStyle(el),
    elLineHeight = _getComputedStyle.lineHeight,
    elFontSize = _getComputedStyle.fontSize;
  var fontSize = elFontSize.split("px")[0];
  var lineHeight = elLineHeight === "normal" ? fontSize * 1.2 : parseFloat(elLineHeight.split("px")[0]);
  var currentHeight = el.clientHeight;
  return Math.ceil(currentHeight / lineHeight);
}

var _excluded = ["state", "onChange"];
var Editor = function Editor(props) {
  var state = props.state,
    _onChange = props.onChange,
    rest = _objectWithoutProperties(props, _excluded);
  var handleSwitchInput = function handleSwitchInput(input) {
    var markdown = state.markdown();
    if (input === "rich-text") {
      _onChange(RichTextState.create(markdown));
    } else if (input === "markdown") {
      _onChange(MarkdownState.create(markdown));
    }
  };
  if (state instanceof RichTextState) {
    return /*#__PURE__*/React.createElement(RichTextEditor, _extends({
      document: state.document,
      onChange: function onChange(document) {
        _onChange(new RichTextState(document));
      },
      users: props.users,
      enableMentions: props.enableMentions,
      enableTemplateVariables: props.enableTemplateVariables,
      enableSchedulingLink: props.enableSchedulingLink,
      onSwitchInput: handleSwitchInput
    }, rest));
  } else if (state instanceof MarkdownState) {
    return /*#__PURE__*/React.createElement(MarkdownEditor, _extends({
      value: state.content,
      onChange: function onChange(value) {
        _onChange(new MarkdownState(value));
      },
      onSwitchInput: handleSwitchInput
    }, rest));
  } else {
    throw new Error("Invalid state prop");
  }
};
Editor.propTypes = {
  state: PropTypes.oneOfType([PropTypes.instanceOf(RichTextState), PropTypes.instanceOf(MarkdownState)]).isRequired,
  onChange: PropTypes.func.isRequired,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  tabIndex: PropTypes.number,
  autoFocus: PropTypes.bool,
  users: PropTypes.array,
  enableMentions: PropTypes.bool,
  enableTemplateVariables: PropTypes.bool,
  enableSchedulingLink: PropTypes.bool,
  renderAboveEditorContent: PropTypes.func,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  uploadImage: PropTypes.func,
  className: PropTypes.string,
  style: PropTypes.object
};

export { Editor, MarkdownState, RichTextState, editorStateToHtml, htmlToEditorState };
